import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Chip from '@material-ui/core/Chip'
import Link from '@material-ui/core/Link'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import FavoriteIcon from '@material-ui/icons/Favorite'
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder'
import {
  Calendar,
  MapPin,
  COACH_PROFILE_META_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import ButtonCta from '@components/atoms/button-cta'
import defaults from '@config/defaults'
import { makeStyles } from '@material-ui/core/styles'
import CoachLogo from '@components/coach-logo/index'
import useIsMobile from '@hooks/useIsMobile'
import useOneTrustFloatingButtonVisibility from '@hooks/useOneTrustFloatingButtonVisibility'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import stripMarkdown from '@lib/strip-markdown'
import {
  formatRegistrationOpensCaption,
  getGroupEventOfferingListCtaLabel,
  shouldShowRegistrationOpensLine,
} from '@lib/pga-coach/group-event-registration'
import GroupProgramOfferingDialog from './group-program-offering-dialog'
import OfferingCoachName from './offering-coach-name'
import {
  getOfferingCardTagChipMuiStyle,
  getOfferingCardTagChipsStyle,
} from './offering-card-tag.styles'

const toTitleFormat = (value = '') => value
  .split(/[\s_-]+/)
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ')

const isSvgImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false
  const path = url.split('?')[0].split('#')[0]
  return path.toLowerCase().endsWith('.svg')
}

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    padding: theme.spacing(1.5, 2),
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      borderColor: '#9aa9bd',
      boxShadow: '0 2px 10px rgba(15, 34, 52, 0.06)',
    },
    '&:hover $image': {
      transform: 'scale(1.05)',
    },
  },
  activeCard: {
    borderColor: '#0a5bd3',
    boxShadow: '0 2px 12px rgba(10, 91, 211, 0.12)',
  },
  topRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  coachMeta: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 0%',
    minWidth: 0,
    gap: theme.spacing(1),
  },
  avatar: {
    width: 40,
    height: 40,
    [theme.breakpoints.down('xs')]: {
      width: 32,
      height: 32,
    },
  },
  coachNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
    flexWrap: 'nowrap',
  },
  coachNameLogoGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
    flex: '1 1 0%',
    flexWrap: 'nowrap',
    '& > img': {
      flexShrink: 0,
    },
  },
  coachName: {
    fontWeight: 700,
    lineHeight: 1.2,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    minWidth: 0,
    maxWidth: '100%',
    flex: '0 1 auto',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  coachNameLink: {
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  coachNameButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    cursor: 'pointer',
  },
  location: {
    fontWeight: 600,
    lineHeight: 1.2,
    fontSize: '1rem',
    color: theme.palette.blue.main,
  },
  coachTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  coachCredentials: {
    display: 'block',
    color: '#6d7784',
    paddingTop: theme.spacing(0.25),
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  otherOfferingLink: {
    fontWeight: 600,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  rightPrice: {
    flexShrink: 0,
    paddingTop: 2,
    textAlign: 'right',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.25),
  },
  favoriteToggle: {
    padding: theme.spacing(0.5),
    '&:focus-visible': {
      outlineOffset: 2,
    },
  },
  price: {
    fontSize: '1.05rem',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  bodyRow: {
    display: 'grid',
    gridTemplateColumns: '112px minmax(0, 1fr) auto',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '80px minmax(0, 1fr)',
      alignItems: 'start',
    },
  },
  image: (props) => ({
    width: '100%',
    height: '100%',
    objectFit: props.isSvg ? 'contain' : 'cover',
    objectPosition: 'center',
    backgroundColor: '#e7eef6',
    transition: 'transform 200ms ease',
  }),
  imageWrap: {
    width: 112,
    height: 96,
    borderRadius: 6,
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      width: 80,
      height: 80,
    },
  },
  middle: {
    minWidth: 0,
  },
  title: {
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5),
  },
  description: {
    color: '#5f6771',
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    marginBottom: theme.spacing(0.75),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  inlineMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: '#4f5966',
    marginTop: 2,
  },
  metaText: {
    fontSize: '0.9rem',
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  registrationOpensMeta: {
    fontSize: '0.9rem',
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  ctaWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexShrink: 0,
    marginTop: 0,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginTop: theme.spacing(1),
      '& a': {
        width: '100%',
        justifyContent: 'center',
      },
    },
  },
  ctaButton: {
    '&.MuiButton-root': {
      borderRadius: 999,
    },
    minHeight: 32,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    lineHeight: 1.2,
    padding: theme.spacing(0.75, 2.25),
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      justifyContent: 'center',
    },
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: '#dfe3ea',
    marginTop: theme.spacing(1),
  },
  footerRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
  chips: {
    ...getOfferingCardTagChipsStyle(theme),
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  favoriteIconSaved: {
    color: theme.palette.blue.main,
  },
  chip: getOfferingCardTagChipMuiStyle(theme),
}))

function GroupProgramOffering({
  rootRef,
  offering,
  isHighlighted,
  onCardSelect,
  onCardKeyDown,
  onHoverStart,
  onHoverEnd,
  onPrimaryActionClick,
  openDetailsFromMapNonce,
  isOfferingFavorite,
  onToggleOfferingFavorite,
  formatPrice,
  formatLocation,
  formatNextAvailability,
  formatJrlDateRange,
  getTagLabels,
  coachInfoPanelEnabled,
  onCoachProfileOpen,
}) {
  const isMobile = useIsMobile()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const historyPushedRef = useRef(false)

  useOneTrustFloatingButtonVisibility(isDialogOpen && isMobile)

  const eventImageUrl = offering.imageUrl || offering.coach.photoUrl || defaults.coachPlaceholder
  const eventImageUrlOptimized = eventImageUrl ? composeOptimizedImageUrl(eventImageUrl, 600) : null
  const isSvg = isSvgImageUrl(eventImageUrlOptimized)
  const classes = useStyles({ isSvg })

  const otherCount = offering.coach.otherOfferingsCount || 0
  const otherLabel = otherCount === 1 ? '1 other offering' : `${otherCount} other offerings`
  const { ctaUrl } = offering
  const listCtaLabel = getGroupEventOfferingListCtaLabel(offering)
  const registrationOpensCaption = shouldShowRegistrationOpensLine(offering)
    ? formatRegistrationOpensCaption(offering.registrationOpenDateTime, offering.timezone)
    : null

  const handleCardClick = (event) => {
    if (typeof event?.target?.closest === 'function') {
      const interactive = event.target.closest('a,button,[role="button"]')
      if (interactive && interactive !== event.currentTarget) return
    }
    onCardSelect?.(event)
    setIsDialogOpen(true)
  }

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      onCardKeyDown?.(event)
      return
    }
    event.preventDefault()
    onCardKeyDown?.(event)
    setIsDialogOpen(true)
  }

  const handleListCtaClick = (event) => {
    event.stopPropagation()
    onPrimaryActionClick?.()
  }

  const handleDialogClose = () => {
    if (historyPushedRef.current) {
      window.history.back()
    } else {
      setIsDialogOpen(false)
    }
  }

  useEffect(() => {
    if (typeof openDetailsFromMapNonce !== 'number' || !Number.isFinite(openDetailsFromMapNonce)) return
    setIsDialogOpen(true)
  }, [openDetailsFromMapNonce])

  useEffect(() => {
    if (!isDialogOpen) return undefined
    window.history.pushState({ offeringModal: true }, '')
    historyPushedRef.current = true
    const handler = () => {
      historyPushedRef.current = false
      setIsDialogOpen(false)
    }
    window.addEventListener('popstate', handler, { once: true })
    return () => window.removeEventListener('popstate', handler)
  }, [isDialogOpen])

  return (
    <>
      <Card
        ref={rootRef}
        className={`${classes.card} ${isHighlighted ? classes.activeCard : ''}`}
        onMouseEnter={() => onHoverStart?.(offering)}
        onMouseLeave={() => onHoverEnd?.(offering)}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={classes.topRow}>
          <div className={classes.coachMeta}>
            <Avatar
              className={classes.avatar}
              src={offering.coach.photoUrl || defaults.coachPlaceholder}
              alt={offering.coach.name || 'Coach'}
            />
            <div className={classes.coachTextBlock}>
              <div className={classes.coachNameRow}>
                <div className={classes.coachNameLogoGroup}>
                  <OfferingCoachName
                    offering={offering}
                    classes={classes}
                    coachInfoPanelEnabled={coachInfoPanelEnabled}
                    onCoachProfileOpen={onCoachProfileOpen}
                  />
                  <CoachLogo memberType={offering.coach.memberType} certificationLevel={offering.coach.certificationLevel} size="16px" />
                </div>
                {otherCount > 0 && offering.profileUrl && (
                  <Link
                    href={offering.profileUrl}
                    className={classes.otherOfferingLink}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {otherLabel}
                  </Link>
                )}
              </div>
              {(offering.coach.coachTitle || offering.coach.credentials) && (
                <Typography variant="caption" className={classes.coachCredentials}>
                  {offering.coach.coachTitle || toTitleFormat(offering.coach.credentials)}
                </Typography>
              )}
            </div>
          </div>

          <div className={classes.rightPrice}>
            <Typography component="span" className={classes.price}>
              {formatPrice(offering)}
            </Typography>
            {typeof onToggleOfferingFavorite === 'function' ? (
              <Tooltip title={isOfferingFavorite ? 'Remove from saved offerings' : 'Save offering'}>
                <IconButton
                  type="button"
                  size="small"
                  aria-label={
                    isOfferingFavorite
                      ? 'Remove offering from saved list'
                      : 'Save offering to your list'
                  }
                  aria-pressed={Boolean(isOfferingFavorite)}
                  className={classes.favoriteToggle}
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    onToggleOfferingFavorite()
                  }}
                >
                  {isOfferingFavorite
                    ? <FavoriteIcon className={classes.favoriteIconSaved} fontSize="small" />
                    : <FavoriteBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
        </div>

        <div className={classes.bodyRow}>
          <div className={classes.imageWrap}>
            <img
              className={classes.image}
              src={eventImageUrlOptimized}
              alt={offering.title}
            />
          </div>

          <div className={classes.middle}>
            <Typography variant="h6" className={classes.title}>
              {offering.title}
            </Typography>
            <Typography className={classes.description}>
              {stripMarkdown(offering.description)}
            </Typography>
            <Box className={classes.inlineMeta} mb={1}>
              <MapPin {...COACH_PROFILE_META_ICON_PROPS} />
              <Typography component="span" className={classes.location}>
                {formatLocation(offering)}
              </Typography>
            </Box>
            <div className={classes.inlineMeta}>
              <Calendar {...COACH_PROFILE_META_ICON_PROPS} />
              <Typography component="span" className={classes.metaText}>
                {offering.format === 'program'
                  ? formatJrlDateRange(offering)
                  : formatNextAvailability(offering)}
              </Typography>
            </div>
            {registrationOpensCaption ? (
              <Typography component="p" className={classes.registrationOpensMeta}>
                {registrationOpensCaption}
              </Typography>
            ) : null}
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.footerRow}>
          <Box className={classes.chips}>
            {getTagLabels(offering).map((label) => (
              <Chip key={label} label={label} size="small" className={classes.chip} />
            ))}
          </Box>

          <div className={classes.ctaWrap}>
            {ctaUrl && (
              <ButtonCta
                href={!/^https?:\/\//.test(ctaUrl) ? ctaUrl : undefined}
                externalLink={/^https?:\/\//.test(ctaUrl) ? ctaUrl : undefined}
                color="primary"
                variant="contained"
                size={isMobile ? 'default' : 'small'}
                onClick={handleListCtaClick}
                className={classes.ctaButton}
              >
                {listCtaLabel}
              </ButtonCta>
            )}
          </div>
        </div>
      </Card>

      <GroupProgramOfferingDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        offering={offering}
        formatPrice={formatPrice}
        formatLocation={formatLocation}
        formatJrlDateRange={formatJrlDateRange}
        getTagLabels={getTagLabels}
        onPrimaryActionClick={onPrimaryActionClick}
        isOfferingFavorite={isOfferingFavorite}
        onToggleOfferingFavorite={typeof onToggleOfferingFavorite === 'function' ? onToggleOfferingFavorite : undefined}
      />
    </>
  )
}

GroupProgramOffering.propTypes = {
  rootRef: PropTypes.shape({ current: PropTypes.any }),
  offering: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    format: PropTypes.string,
    ctaUrl: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaType: PropTypes.string,
    profileUrl: PropTypes.string,
    imageUrl: PropTypes.string,
    venueName: PropTypes.string,
    nextAvailableAt: PropTypes.instanceOf(Date),
    startDateTime: PropTypes.instanceOf(Date),
    endDateTime: PropTypes.instanceOf(Date),
    coachFacilityFullAddress: PropTypes.string,
    maxAttendance: PropTypes.number,
    registrationCount: PropTypes.number,
    registrationFull: PropTypes.bool,
    hasWaitlist: PropTypes.bool,
    geoDistanceMeters: PropTypes.number,
    durationInMinutes: PropTypes.number,
    personaTags: PropTypes.arrayOf(PropTypes.string),
    coach: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      credentials: PropTypes.string,
      coachTitle: PropTypes.string,
      memberType: PropTypes.string,
      certificationLevel: PropTypes.string,
      photoUrl: PropTypes.string,
      otherOfferingsCount: PropTypes.number,
    }),
  }).isRequired,
  isHighlighted: PropTypes.bool,
  onCardSelect: PropTypes.func,
  onCardKeyDown: PropTypes.func,
  onHoverStart: PropTypes.func,
  onHoverEnd: PropTypes.func,
  onPrimaryActionClick: PropTypes.func,
  /** Increments on each map-popup open request for this offering (stable booleans do not retrigger). */
  openDetailsFromMapNonce: PropTypes.number,
  isOfferingFavorite: PropTypes.bool,
  onToggleOfferingFavorite: PropTypes.func,
  formatPrice: PropTypes.func.isRequired,
  formatLocation: PropTypes.func.isRequired,
  formatNextAvailability: PropTypes.func.isRequired,
  formatJrlDateRange: PropTypes.func.isRequired,
  getTagLabels: PropTypes.func.isRequired,
  coachInfoPanelEnabled: PropTypes.bool,
  onCoachProfileOpen: PropTypes.func,
}

GroupProgramOffering.defaultProps = {
  rootRef: undefined,
  isHighlighted: false,
  onCardSelect: undefined,
  onCardKeyDown: undefined,
  onHoverStart: undefined,
  onHoverEnd: undefined,
  onPrimaryActionClick: undefined,
  openDetailsFromMapNonce: undefined,
  isOfferingFavorite: false,
  onToggleOfferingFavorite: undefined,
  coachInfoPanelEnabled: false,
  onCoachProfileOpen: undefined,
}

export default GroupProgramOffering
